'use server';
/**
 * Hero from a photo, created AT UPLOAD (pipeline v2): the photo is uploaded,
 * the guardian consent is recorded, the character record is created with its
 * id and the 3D identity job is enqueued — all in one call. The wizard keeps
 * only the characterId (the order carries ids, never names).
 *
 *   createPhotoHero  — photo + consent (+ name) → a character in the family library
 *   renamePhotoHero  — the name typed on the card
 *   redrawPhotoHero  — the identity failed → draw it again from the same photo
 *   getPhotoHeroStates — drawing / ready / failed per hero (the card polls it)
 */
import { actionErrorMessage } from '@/shared/infrastructure/logging/actionError';
import type { ActionResult } from '@/shared/types/actions';
import { SessionService } from '@/contexts/authentication';
import type { PhotoHeroIdentityState } from '../services/photoHeroIdentity';

export interface PhotoHeroView {
  characterId: string;
  name: string;
  photoKey: string;
  previewUrl: string;
  state: PhotoHeroIdentityState;
  portraitUrl: string | null;
}

export async function createPhotoHero(input: {
  imageBase64: string;
  consentVersion?: string;
  name?: string;
  language?: string;
}): Promise<ActionResult<PhotoHeroView>> {
  try {
    const familyId = await SessionService.getFamilyId();
    const userId = await SessionService.getUserId();
    if (!familyId || !userId) return { success: false, error: 'Not authenticated' };
    if (!input.imageBase64) return { success: false, error: 'Photo is required' };
    // Fail closed: a photo that may depict a child is Art. 9 data.
    if (!input.consentVersion) return { success: false, error: 'PHOTO_CONSENT_REQUIRED' };

    const raw = input.imageBase64.includes(',') ? input.imageBase64.slice(input.imageBase64.indexOf(',') + 1) : input.imageBase64;
    const buffer = Buffer.from(raw, 'base64');
    if (buffer.length === 0) return { success: false, error: 'Invalid photo' };

    const { getImageStorage } = await import('@/contexts/media/storage/CharacterImageStorage');
    const photoKey = await getImageStorage().uploadHeroPhotoDraft(familyId, buffer, 'image/jpeg');
    const { recordPhotoConsent } = await import('../services/recordPhotoConsent');
    await recordPhotoConsent(familyId, photoKey, input.consentVersion);

    const name = (input.name ?? '').trim();
    const { photoHeroFromKey } = await import('../services/photoHeroFromKey');
    const { characterId, queued } = await photoHeroFromKey({
      familyId, userId, photoKey, name, consentVersion: input.consentVersion, language: input.language,
    });
    const { resolveMediaUrl } = await import('@/contexts/media/storage/StorageProvider');
    const previewUrl = await resolveMediaUrl(photoKey).catch(() => '');
    return {
      success: true,
      data: { characterId, name, photoKey, previewUrl, state: queued ? 'drawing' : 'failed', portraitUrl: null },
    };
  } catch (e: unknown) {
    return { success: false, error: actionErrorMessage('createPhotoHero', e) };
  }
}

export async function renamePhotoHero(characterId: string, name: string): Promise<ActionResult<{ characterId: string; name: string }>> {
  try {
    const familyId = await SessionService.getFamilyId();
    if (!familyId) return { success: false, error: 'Not authenticated' };
    const trimmed = name.trim();
    if (!trimmed) return { success: false, error: 'Name is required' };
    const { CharacterService } = await import('../services/CharacterService');
    await CharacterService.updateCharacter({ characterId, familyId, updates: { name: trimmed } });
    return { success: true, data: { characterId, name: trimmed } };
  } catch (e: unknown) {
    return { success: false, error: actionErrorMessage('renamePhotoHero', e) };
  }
}

export async function redrawPhotoHero(input: { characterId: string; photoKey: string; language?: string }): Promise<ActionResult<{ state: PhotoHeroIdentityState }>> {
  try {
    const familyId = await SessionService.getFamilyId();
    if (!familyId) return { success: false, error: 'Not authenticated' };
    const { photoHeroCharacterId, enqueuePhotoHeroIdentity } = await import('../services/photoHeroIdentity');
    // the id is a hash of (family, photo): a key that does not match is not this family's photo
    if (photoHeroCharacterId(familyId, input.photoKey) !== input.characterId) return { success: false, error: 'Not found' };
    const { getCharacterRepository } = await import('../repositories');
    const c = await getCharacterRepository().getById(input.characterId);
    if (!c || c.familyId !== familyId) return { success: false, error: 'Not found' };
    const queued = await enqueuePhotoHeroIdentity({ familyId, characterId: c.id, name: c.name, photoKey: input.photoKey, language: input.language });
    return { success: true, data: { state: queued ? 'drawing' : 'failed' } };
  } catch (e: unknown) {
    return { success: false, error: actionErrorMessage('redrawPhotoHero', e) };
  }
}

/** drawing / ready / failed of the family's photo heroes (only this family's ids answer). */
export async function getPhotoHeroStates(characterIds: string[]): Promise<Array<{ characterId: string; state: PhotoHeroIdentityState; portraitUrl: string | null }>> {
  const familyId = await SessionService.getFamilyId();
  if (!familyId || !characterIds.length) return [];
  const { getCharacterRepository } = await import('../repositories');
  const { identityStateOf } = await import('../services/photoHeroIdentity');
  const repo = getCharacterRepository();
  const out: Array<{ characterId: string; state: PhotoHeroIdentityState; portraitUrl: string | null }> = [];
  for (const id of characterIds.slice(0, 12)) {
    const c = await repo.getById(id).catch(() => null);
    if (!c || c.familyId !== familyId) continue;
    out.push({ characterId: c.id, state: identityStateOf(c.portraitStatus), portraitUrl: c.primaryPortraitUrl ?? null });
  }
  return out;
}
